import React from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Mail, Phone, Trash2, Download, Clock, Users, Inbox } from "lucide-react";

const CITY_LABEL = { paris: "Paris", bergamo: "Bergamo" };
const CITY_CLASS = {
  paris: "bg-[#5BA4D4]/10 text-[#1B2845] border-[#5BA4D4]/40",
  bergamo: "bg-emerald-50 text-emerald-700 border-emerald-300",
};

export default function WaitlistTab() {
  const [entries, setEntries] = React.useState([]);
  const [counts, setCounts] = React.useState({ paris: 0, bergamo: 0, total: 0 });
  const [loading, setLoading] = React.useState(true);
  const [filter, setFilter] = React.useState("all");

  const load = React.useCallback(async () => {
    try {
      const r = await api.get("/admin/waitlist");
      setEntries(r.data.entries || []);
      setCounts(r.data.counts || { paris: 0, bergamo: 0, total: 0 });
    } catch {
      toast.error("Erreur chargement liste d'attente");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette inscription ?")) return;
    try {
      await api.delete(`/admin/waitlist/${id}`);
      toast.success("Inscription supprimée");
      load();
    } catch {
      toast.error("Suppression impossible");
    }
  };

  const exportCsv = () => {
    const rows = [["Ville", "Nom", "Email", "Téléphone", "Message", "Date"]];
    filtered.forEach((e) =>
      rows.push([CITY_LABEL[e.city] || e.city, e.name, e.email, e.phone || "", (e.message || "").replace(/\n/g, " "), e.created_at])
    );
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "pro-pre-waitlist.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = filter === "all" ? entries : entries.filter((e) => e.city === filter);

  const kpis = [
    { label: "Total", value: counts.total, icon: Users, color: "text-[#1B2845]" },
    { label: "Paris", value: counts.paris, icon: MapPin, color: "text-[#5BA4D4]" },
    { label: "Bergamo", value: counts.bergamo, icon: MapPin, color: "text-emerald-600" },
  ];

  if (loading) return <div className="p-8 text-center text-slate-500" data-testid="waitlist-loading">Chargement…</div>;

  return (
    <div className="mt-4" data-testid="waitlist-tab">
      {/* KPI cards */}
      <div className="grid grid-cols-3 gap-3">
        {kpis.map((k) => (
          <div key={k.label} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`waitlist-kpi-${k.label.toLowerCase()}`}>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500">
              <k.icon className={`h-3.5 w-3.5 ${k.color}`} /> {k.label}
            </div>
            <p className={`text-3xl font-bold mt-1 ${k.color}`}>{k.value}</p>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="mt-5 flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-2">
          {["all", "paris", "bergamo"].map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                filter === c ? "bg-[#1B2845] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
              data-testid={`waitlist-filter-${c}`}
            >
              {c === "all" ? "Toutes" : CITY_LABEL[c]}
            </button>
          ))}
        </div>
        <button
          onClick={exportCsv}
          disabled={filtered.length === 0}
          className="btn-outline !py-1.5 !px-3 text-sm disabled:opacity-40"
          data-testid="waitlist-export-csv"
        >
          <Download className="h-4 w-4" /> Exporter CSV
        </button>
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div className="mt-8 text-center text-slate-400 flex flex-col items-center gap-2" data-testid="waitlist-empty">
          <Inbox className="h-10 w-10" />
          <p>Aucune inscription pour le moment.</p>
        </div>
      ) : (
        <div className="mt-4 space-y-2" data-testid="waitlist-list">
          {filtered.map((e) => (
            <div key={e.id} className="rounded-xl border border-slate-200 bg-white p-4 flex items-start justify-between gap-3" data-testid={`waitlist-row-${e.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-navy">{e.name}</span>
                  <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border font-bold ${CITY_CLASS[e.city] || ""}`}>
                    {CITY_LABEL[e.city] || e.city}
                  </span>
                </div>
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
                  <a href={`mailto:${e.email}`} className="flex items-center gap-1 hover:text-[#5BA4D4]"><Mail className="h-3 w-3" /> {e.email}</a>
                  {e.phone ? <a href={`tel:${e.phone}`} className="flex items-center gap-1 hover:text-[#5BA4D4]"><Phone className="h-3 w-3" /> {e.phone}</a> : null}
                  <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {(e.created_at || "").slice(0, 10)}</span>
                </div>
                {e.message ? <p className="mt-1.5 text-sm text-slate-700 italic">“{e.message}”</p> : null}
              </div>
              <button
                onClick={() => remove(e.id)}
                className="text-slate-400 hover:text-red-500 flex-shrink-0"
                title="Supprimer"
                data-testid={`waitlist-delete-${e.id}`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
